import { BillingProcess, LimitNames, PlanTypes } from '@gonfalon/billing/types';
import { enableAIRunsCheckoutDisplay } from '@gonfalon/dogfood-flags/dogfood-flags';

import type { Plan } from '../types';
import { CheckoutProcess } from '../types';

import { aiRunsFeature, freeProduct } from './products';
import {
  abTestsAndExperiments,
  fiveKSessionReplaysAndErrors,
  fiveThousandAIRuns,
  fourteenDayRetention,
  noOverages,
  tenMLogsAndTraces,
  thirtyIdiomaticSdks,
  unlimitedDatasets,
  unlimitedFeatureFlags,
  unlimitedOfflineEvals,
  unlimitedPlaygrounds,
  unlimitedSeats,
} from './sharedFeatures';

// V1: campaign-only — AI runs are display-only (no usage limiting)
const developerProductsV1 = [
  freeProduct(LimitNames.HOSTS, 5, 1),
  freeProduct(LimitNames.MONTHLY_ACTIVE_USERS, 1000, 1000),
  freeProduct(LimitNames.AI_RUNS, 5000, 1000, { displayOnly: true, gatedByFlag: enableAIRunsCheckoutDisplay }),
  freeProduct(LimitNames.SESSIONS, 5000, 1000),
  freeProduct(LimitNames.ERRORS, 5000, 1000),
  freeProduct(LimitNames.TRACES, 10_000_000, 1_000_000),
  freeProduct(LimitNames.LOGS, 10_000_000, 1_000_000),
  freeProduct(LimitNames.EXPERIMENTATION_MAU, 100_000, 0, { isUnbilled: true }),
];

// V2+: AI runs are usage-limited (monthly-billed-only)
const developerProducts = [
  freeProduct(LimitNames.HOSTS, 5, 1),
  freeProduct(LimitNames.MONTHLY_ACTIVE_USERS, 1000, 1000),
  freeProduct(LimitNames.AI_RUNS, 5000, 1000, { monthlyBilledOnly: true }),
  freeProduct(LimitNames.SESSIONS, 5000, 1000),
  freeProduct(LimitNames.ERRORS, 5000, 1000),
  freeProduct(LimitNames.TRACES, 10_000_000, 1_000_000),
  freeProduct(LimitNames.LOGS, 10_000_000, 1_000_000),
  freeProduct(LimitNames.EXPERIMENTATION_MAU, 100_000, 0, { isUnbilled: true }),
];

export const developer2025Plan: Plan = {
  id: PlanTypes.DEVELOPER2025,
  planType: PlanTypes.DEVELOPER2025,
  billingProcess: BillingProcess.STANDARD,
  checkoutProcess: CheckoutProcess.DIRECT_CHECKOUT,
  hasConfigurableLimits: false, // Developer2025 is a fixed plan with no configurable limits
  isFreeDevPlan: true, // Free plan - no payment card required
  isDeveloperSignup: true, // Track as developer signup for analytics
  disableIf: [{ planType: PlanTypes.DEVELOPER2025 }, { planType: PlanTypes.FOUNDATION2025 }],
  marketingCopy: {
    name: 'Developer',
    tagLine: 'A feature-rich foundation for small teams to start building',
    pricingHeadline: 'Free',
    pricingDescription: 'No credit card required. Upgrade any time as you scale.',
    ctaButtonText: 'Select plan',
    checkoutCtaText: 'Get started',
    monthlyPriceText: 'FREE',
    featuresHeader: 'Includes',
    afterTrialTag: {
      text: 'Plan after trial',
      tooltip:
        'After 14 days of full trial access, your account will automatically continue on a free Developer account with limits. No credit card required.',
    },
    features: [
      {
        text: 'Unlimited feature flags',
        tooltip: {
          text: 'Create and manage unlimited feature flags across your applications',
        },
      },
      {
        text: 'Sessions, errors, traces, & logs',
        tooltip: {
          text: '5K sessions, 5K errors, 10M traces, 10M logs included for observability',
        },
      },
      aiRunsFeature,
      {
        text: 'Context-based targeting',
        tooltip: {
          text: 'Target users based on custom attributes and context',
        },
      },
      { text: 'Automated flag cleanup' },
      { text: '5 service connections per month' },
    ],
  },
  versions: {
    1: {
      planTitle: 'Developer Plan',
      products: developerProductsV1,
    },
    // Full platform (CC+AC) — real backend with AI runs
    2: {
      planTitle: 'Developer Plan',
      marketingCopy: {
        tagLine: 'One platform for features and agents. Free to start.',
        pricingDescription: 'No credit card required',
        featuresHeader: '',
        features: [],
        featureGroups: [
          {
            header: 'CodeControl',
            features: [
              unlimitedSeats,
              unlimitedFeatureFlags,
              thirtyIdiomaticSdks,
              fiveKSessionReplaysAndErrors,
              tenMLogsAndTraces,
              abTestsAndExperiments,
            ],
          },
          {
            header: 'AgentControl',
            features: [
              unlimitedSeats,
              unlimitedPlaygrounds,
              unlimitedOfflineEvals,
              unlimitedDatasets,
              fiveThousandAIRuns,
              noOverages,
              fourteenDayRetention,
            ],
          },
        ],
      },
      products: developerProducts,
    },
    // Display-only: AC only features
    3: {
      planTitle: 'Developer Plan',
      marketingCopy: {
        tagLine: 'Start governing AI agents. Free to begin.',
        pricingDescription: 'No credit card required',
        featuresHeader: 'Free plan includes',
        features: [
          unlimitedSeats,
          unlimitedPlaygrounds,
          unlimitedOfflineEvals,
          unlimitedDatasets,
          fiveThousandAIRuns,
          noOverages,
          fourteenDayRetention,
        ],
      },
      products: developerProducts,
    },
  },
};
